import { Upload } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { attritionApi } from '../attritionApi';
import { ATTRITION_REVIEW, type AttritionRequestDetail, type AttritionRequestDocument } from '../attritionTypes';

/*
  The three forms a request's profile opens. Each COLLECTS something, so each is a form with its
  own tick and no dialog in front of it.
*/

const UPLOAD_DESCRIPTION =
  `The scanned document — PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)}. ` +
  "The type is verified against the file's own leading bytes rather than its name.";

/**
 * Submits the file for one checklist line, or replaces one not yet approved.
 *
 * **The line is the modal's subject, not a field** — the card opens this from the row it belongs
 * to. **No Autogenerate**: the one field is a binary whose format the server verifies by magic
 * bytes, and there is no textual format to write honestly — the stated exemption.
 */
export function UploadAttritionDocumentModal({
  request,
  document,
  onClose,
  onSaved,
}: {
  request: AttritionRequestDetail;
  document: AttritionRequestDocument;
  onClose: () => void;
  onSaved: (saved: AttritionRequestDetail) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const file = files[0] ?? null;
  const tooLarge = file !== null && file.size > USER_DOCUMENT_MAX_BYTES;
  const replacing = document.fileName != null;

  async function submit() {
    if (file === null || tooLarge) return;

    setSaving(true);
    setProgress(0);
    setError(null);

    try {
      onSaved(await attritionApi.uploadDocument(request.id, document.id, file, setProgress));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not submit that document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'attrition-document' });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={replacing ? `Replace ${document.name}` : `Upload ${document.name}`}
      size="md"
      id="attrition-document-upload-modal"
      confirm={{ id: 'attrition-document-upload-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="attrition-document-upload-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            id="attrition-document-upload-submit"
            onClick={() => void submit()}
            loading={saving}
            // Real upload progress — the client measures the bytes, so the label says how far it got.
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
            disabled={file === null || tooLarge || !confirmed}
            leading={<Upload className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {/* The CONSEQUENCE, on the form where it is read before the tick. */}
        <p className="text-sm text-slate-600">
          Submitted for <span className="font-medium text-slate-800">{document.name}</span> on{' '}
          {request.requestNumber}. It goes to{' '}
          <span className="font-medium text-slate-800">{document.reviewerName ?? 'its reviewer'}</span> for review
          {replacing ? (
            <>
              , and takes the place of <span className="font-medium text-slate-800">{document.fileName}</span> — the
              previous file is kept in the Repository, no longer listed here
            </>
          ) : null}
          .
        </p>

        <FileField
          id="attrition-document-file"
          label="Document"
          description={UPLOAD_DESCRIPTION}
          files={files}
          onChange={setFiles}
          required
          disabled={saving}
          error={error?.fieldError('file') ?? undefined}
        />
      </div>
    </Modal>
  );
}

/**
 * The line's reviewer approves it, or returns it with a reason.
 *
 * **The decision is the entry point** — the card's Approve and Return glyphs open this with it set,
 * and the TITLE names the act, so the one button reads Submit. A RETURN's reason is required and
 * is what the uploader is told; an approval's note is optional.
 */
export function ReviewAttritionDocumentModal({
  request,
  document,
  decision,
  onClose,
  onSaved,
}: {
  request: AttritionRequestDetail;
  document: AttritionRequestDocument;
  decision: 'approve' | 'return';
  onClose: () => void;
  onSaved: (saved: AttritionRequestDetail) => void;
}) {
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const returning = decision === 'return';
  const valid = !returning || remarks.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : !valid
      ? 'Say why it is being returned.'
      : !confirmed
        ? 'Tick the confirm box to submit.'
        : null;

  async function submit() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await attritionApi.reviewDocument(request.id, document.id, {
          decision: returning ? ATTRITION_REVIEW.returned : ATTRITION_REVIEW.approved,
          remarks: remarks.trim() || null,
        }),
      );
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the review.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'attrition-review' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    setRemarks(returning ? 'The copy is unsigned. Submit the signed original.' : 'Checked against the original.');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${returning ? 'Return' : 'Approve'} ${document.name}`}
      size="md"
      id="attrition-review-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: 'attrition-review-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="attrition-review-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id="attrition-review-submit"
              variant={returning ? 'danger' : 'primary'}
              onClick={() => void submit()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {/* The CONSEQUENCE differs per decision, and is stated before the tick. */}
        <p className="text-sm text-slate-600">
          {returning ? (
            <>
              <span className="font-medium text-slate-800">{document.fileName}</span> goes back to whoever files
              documents on {request.requestNumber}. The request cannot complete until a new file is submitted and
              approved.
            </>
          ) : (
            <>
              <span className="font-medium text-slate-800">{document.fileName}</span> is approved for{' '}
              {request.requestNumber} and can no longer be replaced. When every document is approved, the request is
              completed.
            </>
          )}
        </p>

        <Textarea
          id="attrition-review-remarks"
          label={returning ? 'Reason' : 'Remarks'}
          required={returning}
          description={
            returning
              ? 'What is wrong with the file. This is what the uploader is told.'
              : 'An optional note kept with the approval.'
          }
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={returning && remarks.trim() === '' ? 'Say why it is being returned.' : error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}

/** The request will not go forward. An outcome the register keeps — not a delete. */
export function CancelAttritionRequestModal({
  request,
  onClose,
  onSaved,
}: {
  request: AttritionRequestDetail;
  onClose: () => void;
  onSaved: (saved: AttritionRequestDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = reason.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function submit() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(await attritionApi.cancel(request.id, reason.trim()));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not cancel this request.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'attrition-cancel' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Cancel ${request.requestNumber}`}
      size="md"
      id="attrition-cancel-modal"
      autogenerate={{ onGenerate: () => setReason('The employee withdrew the notice.'), disabled: saving }}
      confirm={{ id: 'attrition-cancel-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="attrition-cancel-close" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={!valid ? 'Say why the request will not go forward.' : !confirmed ? 'Tick the confirm box to submit.' : null}>
            <Button
              id="attrition-cancel-submit"
              variant="danger"
              onClick={() => void submit()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        <p className="text-sm text-slate-600">
          {request.requestNumber} stops: no further document can be submitted or reviewed on it, and it stays in the
          register as cancelled. <strong>This is not deleting it</strong> — the files and reviews so far are kept. A new
          request can then be filed for the employee.
        </p>

        <Textarea
          id="attrition-cancel-reason"
          label="Reason"
          required
          description="Why the request will not go forward. It stays on the request."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() === '' ? 'Say why the request will not go forward.' : error?.fieldError('reason')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
